import { EMPTY_VALUE, labelForRole } from '../internalRoles';

/**
 * Transforms a user summary API model to a view entity
 *
 * @param {import('../types/User.api').IUserSummaryApiModel} model - API model
 * @returns {import('../types/User').IUserSummaryViewEntity} View entity
 */
const toUserSummaryViewEntity = (model) => ({
  id: model.id,
  email: model.email,
  emailStatus: model.emailStatus,
  phoneNumber: model.phoneNumber,
  userStatus: model.userStatus,
  userType: model.userType,
  source: model.source,
  lastActiveAt: model.lastActiveAt,
  createdAt: model.createdAt,
  updatedAt: model.updatedAt,
  firstName: model.firstName,
  lastName: model.lastName,
});

/**
 * Transforms API response to users result
 *
 * @param {import('../types/User.api').IGetUsersByIdsApiResponse} response - API response
 * @returns {import('../types/User').IGetUsersByIdsResult} Users result
 */
const toGetUsersByIdsResult = (response) => ({
  data: response.data.map(toUserSummaryViewEntity),
});

/**
 * Transforms an internal-user API model to a view entity.
 *
 * The `userId` -> `id` mapping is the whole point of this adapter and is the
 * one thing every caller depends on. An internal-user row carries two ids:
 * `id` is the internal_user row's own primary key, while `userId` is the Mongo
 * ObjectId of the user record. Consumers feed the value on through to
 * `PATCH admin/users/:id/dsoManager` and to the job-list `recruiter` filter,
 * both of which address the user record — so picking up the row key would
 * render a correct-looking dropdown that silently fails to assign or filter.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {import('../types/User').IInternalUserViewEntity} View entity
 */
const toInternalUserViewEntity = (model) => ({
  id: model.userId,
  role: model.role,
  email: model.email,
  firstName: model.firstName,
  lastName: model.lastName,
});

/**
 * Transforms the paginated internal-users envelope to a role-holder list.
 *
 * Defaults to `[]` rather than passing undefined through: an undefined list
 * re-arms the `if (!dsoManagerList)` refetch guard in DNTCustomFilters, which
 * is an unbounded fetch loop.
 *
 * @param {import('../types/User.api').IGetInternalUsersApiResponse} response - API response
 * @returns {import('../types/User').IGetInternalUsersResult} Internal users result
 */
const toGetInternalUsersResult = (response) => ({
  data: (response?.data ?? []).map(toInternalUserViewEntity),
});

/**
 * Transforms an internal-user API model to a GoTu Admin table row.
 *
 * Distinct from `toInternalUserViewEntity` because the two answer different
 * questions: that one feeds dropdowns whose value is passed to endpoints
 * addressing the user record, this one feeds a read-only table. Both take `id`
 * from `userId` so the domain has one meaning of `id` throughout.
 *
 * `name` is assembled here rather than in the cell: first_name and last_name
 * are both nullable upstream (the ADM backfill swept in every admin regardless
 * of name state), so a row can legitimately have neither and the table needs
 * something to render. Email is NOT NULL upstream and is the fallback identity.
 *
 * @param {import('../types/User.api').IInternalUserApiModel} model - API model
 * @returns {import('../types/User').IInternalUserRowViewEntity} View entity
 */
const toInternalUserRowViewEntity = (model) => {
  const name = [model.firstName, model.lastName].filter(Boolean).join(' ').trim();

  return {
    id: model.userId,
    name: name || EMPTY_VALUE,
    email: model.email || EMPTY_VALUE,
    role: model.role,
    roleLabel: labelForRole(model.role),
  };
};

/**
 * Transforms the paginated internal-users envelope into a page of table rows.
 *
 * `total` is the server's count before the page limit, which the header count
 * and the pagination stepper both read. It defaults to 0 rather than undefined
 * so `useClampedPage` sees a real total and the stepper renders.
 *
 * @param {import('../types/User.api').IGetInternalUsersApiResponse} response - API response
 * @returns {import('../types/User').IGetInternalUsersPageResult} Page of rows
 */
const toGetInternalUsersPageResult = (response) => ({
  data: (response?.data ?? []).map(toInternalUserRowViewEntity),
  total: response?.total ?? 0,
});

/**
 * Transforms a child office API model to a user view entity
 * Maps snake_case fields to camelCase
 *
 * @param {import('../types/User.api').IChildOfficeApiModel} model - API model (snake_case)
 * @returns {import('../types/User').IUserViewEntity} View entity (camelCase)
 */
const toChildOfficeViewEntity = (model) => ({
  id: model.id,
  averageRating: model.average_rating,
  address:
    model.address?.map((addr) => ({
      formattedAddress: addr.formatted_address,
    })) || [],
  userType: model.user_type,
  officeName: model.office_name,
  accountAdmin: model.account_admin,
  hasW9: model.has_w9,
});

/**
 * Transforms API response to child offices result
 *
 * @param {import('../types/User.api').IGetChildOfficesApiResponse} response - API response (array of offices)
 * @returns {import('../types/User').IGetChildOfficesResult} Child offices result
 */
const toGetChildOfficesResult = (response) => ({
  data: response.map(toChildOfficeViewEntity),
});

/**
 * User Adapters
 * Provides transformation functions between API models and view entities
 */
export const UserAdapters = {
  toUserSummaryViewEntity,
  toGetUsersByIdsResult,
  toInternalUserViewEntity,
  toGetInternalUsersResult,
  toInternalUserRowViewEntity,
  toGetInternalUsersPageResult,
  toChildOfficeViewEntity,
  toGetChildOfficesResult,
};
